import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent
} from "react";
import { useNavigate } from "react-router";
import {
  ApiClientError,
  apiClient,
  type ProjectFolderView,
  type ProjectView
} from "../api/client.js";
import { useAuth } from "../auth/AuthProvider.js";
import { canvasCopy } from "../copy/zh-CN.js";

type CollectionState =
  | { status: "loading" }
  | {
      status: "ready";
      folders: ProjectFolderView[];
      projects: ProjectView[];
    }
  | { status: "offline" }
  | { status: "error"; message: string };

function displayDate(value: string): string {
  return new Intl.DateTimeFormat("zh-CN", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }).format(new Date(value));
}

function roleLabel(role: "member" | "system_admin"): string {
  return role === "system_admin" ? "系统管理员" : "成员";
}

export function ProjectsPage() {
  const navigate = useNavigate();
  const {
    state: authState,
    notice,
    logout,
    recoverAuthentication,
    refreshSession
  } = useAuth();
  const [collection, setCollection] = useState<CollectionState>({
    status: "loading"
  });
  const [projectName, setProjectName] = useState("");
  const [folderId, setFolderId] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string>();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const sidebarRef = useRef<HTMLElement>(null);
  const sidebarCloseRef = useRef<HTMLButtonElement>(null);
  const sidebarTriggerRef = useRef<HTMLButtonElement>(null);
  const restoreSidebarTriggerRef = useRef(false);

  const load = useCallback(async () => {
    setCollection({ status: "loading" });
    try {
      const [folders, projects] = await Promise.all([
        apiClient.listFolders(),
        apiClient.listProjects()
      ]);
      setCollection({
        status: "ready",
        folders,
        projects: [...projects].sort(
          (left, right) =>
            new Date(right.updatedAt).getTime() -
            new Date(left.updatedAt).getTime()
        )
      });
    } catch (cause) {
      if (recoverAuthentication(cause)) return;
      if (cause instanceof TypeError) {
        setCollection({ status: "offline" });
      } else {
        setCollection({
          status: "error",
          message:
            cause instanceof ApiClientError
              ? `项目加载失败（${cause.requestId}）`
              : "项目加载失败。"
        });
      }
    }
  }, [recoverAuthentication]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!sidebarOpen) {
      if (restoreSidebarTriggerRef.current) {
        restoreSidebarTriggerRef.current = false;
        sidebarTriggerRef.current?.focus();
      }
      return;
    }

    restoreSidebarTriggerRef.current = true;
    const sidebar = sidebarRef.current;
    const focusCloseButton = () => sidebarCloseRef.current?.focus();
    const focusFrame = requestAnimationFrame(focusCloseButton);
    const keepFocusInside = (event: FocusEvent) => {
      if (
        sidebar &&
        event.target instanceof Node &&
        !sidebar.contains(event.target)
      ) {
        focusCloseButton();
      }
    };
    const trapKeyboardFocus = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setSidebarOpen(false);
        return;
      }
      if (event.key !== "Tab" || !sidebar) return;

      const focusable = Array.from(
        sidebar.querySelectorAll<HTMLElement>(
          'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => !element.hidden);
      const first = focusable.at(0);
      const last = focusable.at(-1);
      if (!first || !last) {
        event.preventDefault();
        return;
      }
      if (
        event.shiftKey &&
        (document.activeElement === first ||
          !sidebar.contains(document.activeElement))
      ) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        (document.activeElement === last ||
          !sidebar.contains(document.activeElement))
      ) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("focusin", keepFocusInside);
    document.addEventListener("keydown", trapKeyboardFocus);
    return () => {
      cancelAnimationFrame(focusFrame);
      document.removeEventListener("focusin", keepFocusInside);
      document.removeEventListener("keydown", trapKeyboardFocus);
    };
  }, [sidebarOpen]);

  const folders = collection.status === "ready" ? collection.folders : [];
  const projects = collection.status === "ready" ? collection.projects : [];
  const folderNames = useMemo(
    () => new Map(folders.map((folder) => [folder.id, folder.name])),
    [folders]
  );

  if (authState.status !== "authenticated") return null;

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const name = projectName.trim();
    if (!name) {
      setCreateError("请输入项目名称。");
      return;
    }
    if (name.length > 120) {
      setCreateError("项目名称不能超过 120 个字符。");
      return;
    }

    setCreating(true);
    setCreateError(undefined);
    try {
      const input = {
        name,
        folderId: folderId || null
      };
      let project: ProjectView;
      try {
        project = await apiClient.createProject(input);
      } catch (cause) {
        if (
          cause instanceof ApiClientError &&
          cause.code === "INVALID_CSRF_TOKEN"
        ) {
          await refreshSession();
          project = await apiClient.createProject(input);
        } else {
          throw cause;
        }
      }
      navigate(`/projects/${project.id}`);
    } catch (cause) {
      if (recoverAuthentication(cause)) {
        return;
      } else if (cause instanceof TypeError) {
        setCreateError("网络连接不可用，请恢复连接后重试。");
      } else if (cause instanceof ApiClientError && cause.status === 409) {
        setCreateError("项目发生冲突，请刷新列表后重试。");
      } else if (
        cause instanceof ApiClientError &&
        cause.code === "INVALID_CSRF_TOKEN"
      ) {
        setCreateError("会话已更新，请重新加载页面后重试。");
      } else {
        setCreateError("项目创建失败，请稍后重试。");
      }
    } finally {
      setCreating(false);
    }
  }

  async function signOut() {
    try {
      await logout();
      navigate("/login", { replace: true });
    } catch {
      // The provider preserves the authenticated state and exposes a retry notice.
    }
  }

  return (
    <main className="foundation-shell">
      <aside
        ref={sidebarRef}
        className={`foundation-sidebar${sidebarOpen ? " foundation-sidebar--open" : ""}`}
        aria-label="工作区导航"
        role={sidebarOpen ? "dialog" : undefined}
        aria-modal={sidebarOpen ? true : undefined}
      >
        <button
          ref={sidebarCloseRef}
          className="foundation-icon-button foundation-sidebar-close"
          type="button"
          aria-label="收起导航"
          onClick={() => setSidebarOpen(false)}
        >
          <span aria-hidden="true">×</span>
        </button>
        <div className="foundation-sidebar__brand">
          <span aria-hidden="true">S</span>
          <div>
            <strong>Seedance Studio</strong>
            <small>创作工作区</small>
          </div>
        </div>
        <nav>
          <a className="foundation-nav-link foundation-nav-link--active" href="/projects">
            <span aria-hidden="true">◆</span>
            我的项目
          </a>
          <a className="foundation-nav-link" href="/generations">
            <span aria-hidden="true">◷</span>
            我的生成记录
          </a>
          {authState.user.role === "system_admin" ? (
            <a className="foundation-nav-link" href="/admin/members">
              <span aria-hidden="true">◇</span>
              管理后台
            </a>
          ) : null}
        </nav>
        <div className="foundation-account">
          <span className="foundation-account__avatar" aria-hidden="true">
            {authState.user.username.slice(0, 1).toUpperCase()}
          </span>
          <div>
            <strong>{authState.user.username}</strong>
            <small>{roleLabel(authState.user.role)}</small>
          </div>
        </div>
      </aside>
      {sidebarOpen ? (
        <button
          className="foundation-sidebar-backdrop"
          type="button"
          aria-label="关闭导航"
          onClick={() => setSidebarOpen(false)}
        />
      ) : null}

      <section
        className="foundation-content"
        inert={sidebarOpen ? true : undefined}
        aria-hidden={sidebarOpen ? true : undefined}
      >
        <header className="foundation-header">
          <button
            ref={sidebarTriggerRef}
            className="foundation-icon-button foundation-sidebar-toggle"
            type="button"
            aria-label="展开导航"
            aria-expanded={sidebarOpen}
            onClick={() => setSidebarOpen(true)}
          >
            <span aria-hidden="true">{sidebarOpen ? "×" : "☰"}</span>
          </button>
          <div>
            <p className="foundation-eyebrow">私有工作区</p>
            <h1>我的项目</h1>
          </div>
          <button
            className="foundation-button foundation-button--ghost"
            type="button"
            onClick={() => void signOut()}
          >
            退出登录
          </button>
        </header>
        {notice ? (
          <p className="foundation-alert foundation-alert--error" role="alert">
            {notice}
          </p>
        ) : null}

        <section className="foundation-create-panel" aria-labelledby="create-project-title">
          <div>
            <p className="foundation-eyebrow">新建项目</p>
            <h2 id="create-project-title">开始新的创作</h2>
          </div>
          <form className="foundation-create-form" onSubmit={submit}>
            <label>
              <span>新项目名称</span>
              <input
                value={projectName}
                onChange={(event) => setProjectName(event.target.value)}
                placeholder="例如：第 01 集"
                disabled={creating}
              />
            </label>
            <label>
              <span>项目文件夹（可选）</span>
              <select
                value={folderId}
                onChange={(event) => setFolderId(event.target.value)}
                disabled={creating}
              >
                <option value="">未归档</option>
                {folders.map((folder) => (
                  <option key={folder.id} value={folder.id}>
                    {folder.name}
                  </option>
                ))}
              </select>
            </label>
            <button
              className="foundation-button foundation-button--primary"
              type="submit"
              disabled={creating}
            >
              {creating ? "正在创建…" : `${canvasCopy.createProject}并${canvasCopy.openCanvas}`}
            </button>
          </form>
          {createError ? (
            <p className="foundation-alert foundation-alert--error" role="alert">
              {createError}
            </p>
          ) : null}
        </section>

        <section className="foundation-projects" aria-labelledby="recent-projects-title">
          <div className="foundation-section-heading">
            <div>
              <p className="foundation-eyebrow">最近使用</p>
              <h2 id="recent-projects-title">最近项目</h2>
            </div>
            {collection.status === "ready" ? (
              <span>{projects.length} 个项目</span>
            ) : null}
          </div>

          {collection.status === "loading" ? (
            <div className="foundation-state-card" aria-live="polite">
              <span className="foundation-spinner" aria-hidden="true" />
              <strong>正在加载项目…</strong>
            </div>
          ) : null}
          {collection.status === "offline" ? (
            <div className="foundation-state-card" role="alert">
              <strong>网络连接不可用</strong>
              <p>项目仍然安全保存在服务器。恢复连接后即可重试。</p>
              <button
                className="foundation-button foundation-button--ghost"
                type="button"
                onClick={() => void load()}
              >
                重试
              </button>
            </div>
          ) : null}
          {collection.status === "error" ? (
            <div className="foundation-state-card" role="alert">
              <strong>{collection.message}</strong>
              <p>请稍后再次获取最新项目。</p>
              <button
                className="foundation-button foundation-button--ghost"
                type="button"
                onClick={() => void load()}
              >
                重试
              </button>
            </div>
          ) : null}
          {collection.status === "ready" && projects.length === 0 ? (
            <div className="foundation-state-card">
              <strong>这里还没有项目</strong>
              <p>输入名称，创建你的第一个私有创作空间。</p>
            </div>
          ) : null}
          {collection.status === "ready" && projects.length > 0 ? (
            <div className="foundation-project-grid">
              {projects.map((project) => (
                <a
                  className="foundation-project-card"
                  href={`/projects/${project.id}`}
                  key={project.id}
                >
                  <span className="foundation-project-card__visual" aria-hidden="true">
                    <i />
                    <i />
                    <i />
                  </span>
                  <span className="foundation-project-card__body">
                    <strong>{project.name}</strong>
                    <small>
                      {project.folderId
                        ? folderNames.get(project.folderId) ?? "项目文件夹"
                        : "未归档"}
                      {" · "}
                      {displayDate(project.updatedAt)}
                    </small>
                  </span>
                  <span aria-hidden="true">↗</span>
                </a>
              ))}
            </div>
          ) : null}
        </section>
      </section>
    </main>
  );
}
